import { useState } from 'react';
import { Plus, Trash2, Pencil, GripVertical } from 'lucide-react';
import {
  useCustomFields,
  useCreateCustomField,
  useUpdateCustomField,
  useDeleteCustomField,
} from '@/hooks/useCustomFields';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  Select,
  Skeleton,
  Tabs,
  Textarea,
} from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { CustomFieldDefinition, CustomFieldEntityType, CustomFieldType } from '@/types';

const entityTypeLabels: Record<CustomFieldEntityType, string> = {
  CONTACT: 'Contact',
  ORGANIZATION: 'Organization',
  DEAL: 'Deal',
};

const fieldTypeLabels: Record<CustomFieldType, string> = {
  TEXT: 'Text',
  NUMBER: 'Number',
  DATE: 'Date',
  SELECT: 'Select',
  BOOLEAN: 'Boolean',
  URL: 'URL',
};

const fieldTypeColors: Record<CustomFieldType, 'default' | 'primary' | 'info' | 'warning' | 'success' | 'error'> = {
  TEXT: 'default',
  NUMBER: 'info',
  DATE: 'warning',
  SELECT: 'primary',
  BOOLEAN: 'success',
  URL: 'info',
};

const entityTabs: CustomFieldEntityType[] = ['CONTACT', 'ORGANIZATION', 'DEAL'];

export default function CustomFieldSettings() {
  const [activeEntity, setActiveEntity] = useState<CustomFieldEntityType>('CONTACT');
  const { data: fields, isLoading } = useCustomFields(activeEntity);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  const fieldList = (
    <div className="space-y-2">
      {(fields ?? []).length === 0 ? (
        <p className="text-sm text-grey-500 py-4 text-center">
          No custom fields defined for {entityTypeLabels[activeEntity].toLowerCase()}s yet.
        </p>
      ) : (
        (fields ?? []).map((field) => (
          <FieldRow
            key={field.id}
            field={field}
            onEdit={() => setEditing(field)}
          />
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Custom Fields"
          subtitle="Define custom data fields for contacts, organizations, and deals."
          actions={
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-3.5 h-3.5" />
              Add Field
            </Button>
          }
        />

        <CardBody>
          {/* Entity type tabs */}
          <Tabs
            value={activeEntity}
            onValueChange={(value) => setActiveEntity(value as CustomFieldEntityType)}
            items={entityTabs.map((type) => ({
              value: type,
              label: entityTypeLabels[type],
              content: fieldList,
            }))}
          />
        </CardBody>
      </Card>

      {showCreate && (
        <CreateFieldDialog
          open={showCreate}
          onOpenChange={setShowCreate}
          defaultEntityType={activeEntity}
        />
      )}

      {editing && (
        <EditFieldDialog
          field={editing}
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </div>
  );
}

function FieldRow({
  field,
  onEdit,
}: {
  field: CustomFieldDefinition;
  onEdit: () => void;
}) {
  const deleteField = useDeleteCustomField();

  const handleDelete = async () => {
    if (!confirm(`Delete custom field "${field.label}"? This will not remove existing field values from records.`)) return;
    await deleteField.mutateAsync(field.id);
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-grey-50 group">
      <GripVertical className="w-4 h-4 text-grey-300 cursor-grab" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-secondary">{field.label}</span>
          <Badge variant={fieldTypeColors[field.fieldType]} size="sm">
            {fieldTypeLabels[field.fieldType]}
          </Badge>
          {field.isRequired && (
            <Badge variant="error" size="sm">Required</Badge>
          )}
          {!field.isActive && (
            <Badge variant="default" size="sm">Inactive</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-grey-400 font-mono">{`{{${field.name}}}`}</span>
          {field.description && (
            <span className="text-xs text-grey-500">{field.description}</span>
          )}
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
          aria-label={`Edit ${field.label}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleDelete}
          className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
          aria-label={`Delete ${field.label}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateFieldDialog({
  open,
  onOpenChange,
  defaultEntityType,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEntityType: CustomFieldEntityType;
}) {
  const [form, setForm] = useState({
    label: '',
    fieldType: 'TEXT' as CustomFieldType,
    entityType: defaultEntityType,
    options: '',
    isRequired: false,
    description: '',
  });
  const createField = useCreateCustomField();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createField.mutateAsync({
      label: form.label,
      fieldType: form.fieldType,
      entityType: form.entityType,
      options: form.fieldType === 'SELECT' ? form.options.split('\n').map((o) => o.trim()).filter(Boolean) : undefined,
      isRequired: form.isRequired,
      description: form.description || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Custom Field"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="create-custom-field-form" loading={createField.isPending}>Add Field</Button>
        </>
      }
    >
      <form id="create-custom-field-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Label"
          required
          placeholder="e.g. Company Size Range"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Field Type"
            value={form.fieldType}
            onChange={(e) => setForm({ ...form, fieldType: e.target.value as CustomFieldType })}
            options={Object.entries(fieldTypeLabels).map(([value, label]) => ({ value, label }))}
          />
          <Select
            label="Entity Type"
            value={form.entityType}
            onChange={(e) => setForm({ ...form, entityType: e.target.value as CustomFieldEntityType })}
            options={Object.entries(entityTypeLabels).map(([value, label]) => ({ value, label }))}
          />
        </div>
        {form.fieldType === 'SELECT' && (
          <Textarea
            label="Options (one per line)"
            required
            placeholder="Option 1&#10;Option 2&#10;Option 3"
            value={form.options}
            onChange={(e) => setForm({ ...form, options: e.target.value })}
            rows={4}
          />
        )}
        <Textarea
          label="Description"
          placeholder="Help text shown to users..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <label className="flex items-center gap-2 text-sm text-grey-700">
          <input
            type="checkbox"
            checked={form.isRequired}
            onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
            className="rounded border-grey-300"
          />
          Required field
        </label>
      </form>
    </Modal>
  );
}

function EditFieldDialog({
  field,
  open,
  onOpenChange,
}: {
  field: CustomFieldDefinition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    label: field.label,
    options: field.fieldType === 'SELECT' && Array.isArray(field.options) ? field.options.join('\n') : '',
    isRequired: field.isRequired,
    isActive: field.isActive,
    description: field.description || '',
  });
  const updateField = useUpdateCustomField();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateField.mutateAsync({
      id: field.id,
      data: {
        label: form.label,
        options: field.fieldType === 'SELECT' ? form.options.split('\n').map((o) => o.trim()).filter(Boolean) : undefined,
        isRequired: form.isRequired,
        isActive: form.isActive,
        description: form.description || undefined,
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Custom Field"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="edit-custom-field-form" loading={updateField.isPending}>Save Changes</Button>
        </>
      }
    >
      <form id="edit-custom-field-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Label"
          required
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="label">Field Type</p>
            <p className="text-sm text-grey-500">{fieldTypeLabels[field.fieldType]}</p>
          </div>
          <div>
            <p className="label">Entity Type</p>
            <p className="text-sm text-grey-500">{entityTypeLabels[field.entityType]}</p>
          </div>
        </div>
        {field.fieldType === 'SELECT' && (
          <Textarea
            label="Options (one per line)"
            required
            value={form.options}
            onChange={(e) => setForm({ ...form, options: e.target.value })}
            rows={4}
          />
        )}
        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-grey-700">
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
              className="rounded border-grey-300"
            />
            Required field
          </label>
          <label className="flex items-center gap-2 text-sm text-grey-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="rounded border-grey-300"
            />
            Active
          </label>
        </div>
      </form>
    </Modal>
  );
}
